import { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { LoadingSpinner, TabMenu, InlineAlert } from '../../../components/BaseComponents'
import PageHeadings from '../../../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../../../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../../../components/Router/routes'
import { getPeriodStatistics } from '../../../redux/actions/periodStatisticsActions'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks/reduxHooks'
import {
	PERIODSTATS_RESET,
	selectAllPeriodStatistics,
	selectPeriodStatisticsStatus,
} from '../../../redux/slices/periodStatisticsSlice'
import { FetchStatus } from '../../../types/LoadingStates'
import SupplierInsightsEmissionQualityGraph from '../Suppliers/Trends/Graphs/QualityRating'
import CompanyInsightsOverviewGraph from './Graphs/Overview'
import CompanyInsightsScope1Graph from './Graphs/Scope1'
import CompanyInsightsScope2Graph from './Graphs/Scope2'
import CompanyInsightsScope3Graph from './Graphs/Scope3'
import useApi from '../../../hooks/useApi'

const CompanyInsightsTrends = () => {
	const dispatch = useAppDispatch()
	const api = useApi();

	const endpointStatus = useAppSelector(selectPeriodStatisticsStatus)
	const allPeriodStatistics = useAppSelector(selectAllPeriodStatistics)

	// Reset data
	useEffect(() => {
		dispatch({ type: PERIODSTATS_RESET })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Load accounting periods
	useEffect(() => {
		if (endpointStatus === FetchStatus.initial) {
			dispatch(getPeriodStatistics(api))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endpointStatus])

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companyInsightsTrends.name}</title>
			</Helmet>

			<PageHeadings crumbs={[{ label: routes.companyInsightsTrends.menuName as string }]} />

			{endpointStatus === FetchStatus.loading && (
				<LoadingSpinner loadingText='Loading your statistics...' />
			)}

			{endpointStatus === FetchStatus.loaded && (
				<>
					{allPeriodStatistics.length > 0 ? (
						<TabMenu
							ariaLabel='Emission breakdowns by scope'
							additionalClass='mt-8'
							additionalPanelClass='p-4'
							tabs={[
								{
									id: 'total',
									label: 'Total Emissions by Scopes',
									panel: (active) => (
										<>
										<h2 className='text-xl text-white mb-4'>
												Total Emissions by Scopes
											</h2>
											<div className='p-4 max-w-6xl'>
												{active && <CompanyInsightsOverviewGraph chartData={allPeriodStatistics} />}
											</div>
										</>
									),
								},
								{
									id: 'scope-1',
									label: 'Scope 1',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>Scope 1 Breakdown</h2>
											<div className='p-4 max-w-6xl'>
												{active && <CompanyInsightsScope1Graph chartData={allPeriodStatistics} />}
											</div>
										</>
									),
								},
								{
									id: 'scope-2',
									label: 'Scope 2',
									panel: (active) => (
										<>
											<h2 className='text-xl text-white mb-4'>Scope 2 Breakdown</h2>
											<div className='p-4 max-w-6xl'>
												{active && <CompanyInsightsScope2Graph chartData={allPeriodStatistics} />}
											</div>
										</>
									),
								},
								{
									id: 'scope-3',
									label: 'Scope 3 by Sector',
									panel: (active) => (
										<>
										<h2 className='text-xl text-white mb-4'>
												Scope 3 Breakdown by Sector
											</h2>
											<div className='p-4 max-w-6xl'>
												{active && <CompanyInsightsScope3Graph chartData={allPeriodStatistics} />}
											</div>
										</>
									),
								},
								{
									id: 'data-quality',
									label: 'Data Quality',
									panel: (active) => (
										<>
										<h2 className='text-xl text-white mb-4'>
												Data Quality
											</h2>
											<div className='p-4 max-w-6xl'>
												{active && <SupplierInsightsEmissionQualityGraph chartData={allPeriodStatistics} />}
											</div>
										</>
									),
								},
							]}
						/>
					) : (
						<div className='rounded-lg bg-charcoal-600 p-4 mt-8'>
							<p className='text-white'>
								Sorry! We couldn't find any completed accounting periods to compare
							</p>
						</div>
					)}
				</>
			)}

			{endpointStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching accounting periods associated with this organisation. If this
						problem persists, please contact the Neoni team.
					</p>
				</InlineAlert>
			)}
		</PageTemplate>
	)
}

export default CompanyInsightsTrends
